import { DimensionDataWithDomain, DomainInfo, PlotAxis } from './plot-data.state'
import { map, Subject } from 'rxjs'

export interface PlotData {
    domainX: DomainInfo | null
    domainY: DomainInfo | null
    domainColor: DomainInfo | null
    // id, x, y, color (continuous third axis of scatter plot or categorical for other cases)
    points: [number, number, number, number][]
    legend: { key: number; value: string }[] | null
}

interface InternalPlotData {
    domainX: DomainInfo | null
    domainY: DomainInfo | null
    domainColor: DomainInfo | null
    points: [number, number, number, number][]
    pointsThisLevel: number[] | null
    pointsLowerLevels: number[] | null
    pointType: 0 | 1 | 2
    legend: { key: number; value: string }[] | null
}

let _plotData: InternalPlotData = {
    domainX: null,
    domainY: null,
    domainColor: null,
    points: [],
    pointsThisLevel: null,
    pointsLowerLevels: null,
    pointType: 0,
    legend: null,
}

const _internalPlotData$ = new Subject<InternalPlotData>()

export const plotData$ = _internalPlotData$.pipe(
    map((data: InternalPlotData): PlotData => {
        // This function prepares the point for displaying in the scatter plot
        // For free selections, the all points are shown, either colored (thrid axis) or not
        // For hierarchical selections, we choose the points to show and their parents and children
        // If the third axis is chosen in hierarchical mode, we can only show the current level
        console.log(data)

        return {
            domainX: data.domainX,
            domainY: data.domainY,
            domainColor: data.domainColor,
            points: data.points,
            legend: data.legend,
        }

        // Split and color points for hierarchical
        // In current selected level
        let showRegularly: [number, number, number, number][]
        let pointsToShowRegularly = new Set(data.pointsThisLevel)
        if (data.pointsThisLevel) {
            showRegularly = data.points
                .filter(point => pointsToShowRegularly.has(point[0]))
                .map(point => [point[0], point[1], point[2], 0])
        } else {
            pointsToShowRegularly = new Set(data.points.map(point => point[0]))
            showRegularly = data.points.map(point => [point[0], point[1], point[2], 0])
        }

        // In levels below
        const childrenAndBelowPoints = new Set(data.pointsLowerLevels)
        const childrenAndBelow: [number, number, number, number][] = data.points
            .filter(point => childrenAndBelowPoints.has(point[0]))
            .map(point => [point[0], point[1], point[2], 2])

        // In levels above or sideways in the tree
        const pointsShownThroughSelfAndChildren = new Set([
            ...pointsToShowRegularly,
            ...childrenAndBelowPoints,
        ])
        const parentsAndOthers: [number, number, number, number][] = data.points
            .filter(point => !pointsShownThroughSelfAndChildren.has(point[0]))
            .map(point => [point[0], point[1], point[2], 1])

        // Merge all points
        const displayPoints = [...showRegularly, ...childrenAndBelow, ...parentsAndOthers]
        console.log(data, showRegularly, childrenAndBelow, parentsAndOthers)

        return {
            domainX: data.domainX,
            domainY: data.domainY,
            domainColor: data.domainColor,
            points: displayPoints,
            legend: data.legend,
        }
    })
)

export const plotFilterData$ = _internalPlotData$.pipe(
    map((data: InternalPlotData): Set<number> | null => {
        // This function prepares the selectable points for the scatter plot
        if (data.pointType == 0) {
            // Do not filter in free selection mode
            return null
        }
        if (data.pointsThisLevel) return new Set(data.pointsThisLevel)
        if (data.pointsLowerLevels) {
            // If we are on the root of a selection node
            const pointsLowerLevels = new Set(data.pointsLowerLevels)
            const selectablePoints = data.points
                .filter(point => !pointsLowerLevels.has(point[0]))
                .map(point => point[0])
            return new Set<number>(selectablePoints)
        }
        return null
    })
)

const setPlotData = (data: InternalPlotData): void => {
    _plotData = data
    _internalPlotData$.next(_plotData)
}

export const resetPlotData = (): void => {
    setPlotData({
        domainX: null,
        domainY: null,
        domainColor: null,
        points: [],
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: null,
    })
}

export const setPlotDataType = (type: 0 | 1 | 2): void => {
    setPlotData({
        domainX: _plotData.domainX,
        domainY: _plotData.domainY,
        domainColor: _plotData.domainColor,
        points: _plotData.points,
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: type,
        legend: _plotData.legend,
    })
}

export const setPlotDataForBothAxes = (
    dataX: DimensionDataWithDomain,
    dataY: DimensionDataWithDomain
): void => {
    if (dataX.points.length !== dataY.points.length) {
        throw new Error('Data size mismatch for X / Y axes, discarding previous data')
    }
    setPlotData({
        domainX: dataX.domain,
        domainY: dataY.domain,
        domainColor: null,
        points: dataX.points.map((e, i) => [e[0], e[1], dataY.points[i][1], 0]),
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: null,
    })
}

export const setPlotDataForAxis = (data: DimensionDataWithDomain | null, axis: PlotAxis): void => {
    // incoming data format [segment_id, value]
    // plot data format [segment_id, x_value, y_value, color_value]
    if (!data) {
        resetAxisKeepOthers(axis)
        return
    }
    const resetOtherAxis =
        (axis === PlotAxis.X && !_plotData.domainY) || (axis === PlotAxis.Y && !_plotData.domainX)
    resetOtherAxis
        ? setPlotDataForOneAxisResetOther(data, axis)
        : setPlotDataForOneAxisKeepOther(data, axis)
}

export const filterPlotData = (
    pointsThisLevel: Set<number> | null,
    pointsLowerLevels: Set<number> | null
): void => {
    setPlotData({
        domainX: _plotData.domainX,
        domainY: _plotData.domainY,
        domainColor: _plotData.domainColor,
        points: _plotData.points,
        pointsThisLevel: pointsThisLevel ? Array.from(pointsThisLevel.values()) : null,
        pointsLowerLevels: pointsLowerLevels ? Array.from(pointsLowerLevels.values()) : null,
        pointType: _plotData.pointType,
        legend: _plotData.legend,
    })
}

export const setMultipleInstanceData = (selections: any): void => {
    console.log(selections, _plotData)
    const updatedPoints = _plotData.points.map(p => {
        const indexOfSelection = selections.findIndex((s: any) => s.points.has(p[0]))
        return [p[0], p[1], p[2], indexOfSelection >= 0 ? indexOfSelection + 2 : 0] as [
            number,
            number,
            number,
            number
        ]
    })

    console.log(updatedPoints)
    setPlotData({
        ..._plotData,
        points: updatedPoints,
    })
}

const setPlotDataForOneAxisResetOther = (data: DimensionDataWithDomain, axis: PlotAxis): void => {
    setPlotData({
        domainX: axis === PlotAxis.X ? data.domain : null,
        domainY: axis === PlotAxis.Y ? data.domain : null,
        domainColor: axis === PlotAxis.COLOR ? data.domain : null,
        points: data.points.map((point: [number, number]): [number, number, number, number] => {
            return axis === PlotAxis.X ? [point[0], point[1], 0, 0] : [point[0], 0, point[1], 0]
        }),
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: axis === PlotAxis.COLOR ? data.legend! : null,
    })
}

const setPlotDataForOneAxisKeepOther = (data: DimensionDataWithDomain, axis: PlotAxis): void => {
    if (_plotData.points.length !== data.points.length) {
        throw new Error('Data size mismatch for X / Y axes, discarding previous data')
    }
    setPlotData({
        domainX: axis === PlotAxis.X ? data.domain : _plotData.domainX,
        domainY: axis === PlotAxis.Y ? data.domain : _plotData.domainY,
        domainColor: axis === PlotAxis.COLOR ? data.domain : _plotData.domainColor,
        points: data.points.map(
            (point: [number, number], i: number): [number, number, number, number] => {
                if (point[0] !== _plotData.points[i][0]) throw new Error('Segment IDs do not match')
                if (axis === PlotAxis.X) {
                    return [point[0], point[1], _plotData.points[i][2], _plotData.points[i][3]]
                } else if (axis === PlotAxis.Y) {
                    return [point[0], _plotData.points[i][1], point[1], _plotData.points[i][3]]
                } else {
                    return [point[0], _plotData.points[i][1], _plotData.points[i][2], point[1]]
                }
            }
        ),
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: axis === PlotAxis.COLOR ? data.legend! : _plotData.legend,
    })
}

const resetAxisKeepOthers = (axis: PlotAxis): void => {
    setPlotData({
        domainX: axis === PlotAxis.X ? null : _plotData.domainX,
        domainY: axis === PlotAxis.Y ? null : _plotData.domainY,
        domainColor: axis === PlotAxis.COLOR ? null : _plotData.domainColor,
        points: _plotData.points.map(
            (point: [number, number, number, number]): [number, number, number, number] => {
                if (axis === PlotAxis.X) {
                    return [point[0], 0, point[2], point[3]]
                } else if (axis === PlotAxis.Y) {
                    return [point[0], point[1], 0, point[3]]
                } else {
                    return [point[0], point[1], point[2], 0]
                }
            }
        ),
        pointsThisLevel: _plotData.pointsThisLevel,
        pointsLowerLevels: _plotData.pointsLowerLevels,
        pointType: _plotData.pointType,
        legend: axis === PlotAxis.COLOR ? null : _plotData.legend,
    })
}
